import type { jsPDF } from 'jspdf';
import { EvictionsByYearPerMonth, MONTH_ORDER, ReportFilters } from '../../../types/types';
import { computeEvictionMetrics } from '../../../utils/summary_report/metrics/evictionMetrics';
import {
  drawSectionHeader,
  drawMetricCard,
  ensureSpace,
  drawBarChart,
  drawLineChart,
  drawLegend,
  drawDataList,
  drawCardTitle,
} from '../../../utils/summary_report/pdfPrimitives';
import { colors, layout } from '../../../theme/themes';
import { houseIcon, personIcon } from '../../../utils/summary_report/icon';

/** The side data list shows at most this many months — the most recent ones up to the cutoff. Charts themselves are never capped. */
const MAX_CHART_POINTS = 5;

function formatNumber(n: number): string {
  return n.toLocaleString('en-US');
}

/** "SUMMARY" caption above each chart's 5-month data list, matching the
 * uppercase stone-400 caption style used for "YEAR TO DATE" / "THIS MONTH". */
function drawSummaryCaption(doc: jsPDF, x: number, y: number): void {
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...colors.stone400);
  doc.text('SUMMARY', x, y);
}

/**
 * Draws section 01 (eviction numbers) starting at `startY` and returns the
 * y-coordinate the next section should start at.
 */
export function drawEvictionNumbersSection(
  doc: jsPDF,
  data: EvictionsByYearPerMonth,
  filters: ReportFilters,
  startY: number,
  contentWidth: number,
  icons?: { households?: string; individuals?: string },
): number {
  const x = layout.pageMarginX;
  const metrics = computeEvictionMetrics(data, filters);
  const includesMonth = filters.mode === 'month' || filters.mode === 'both';

  const cardGap = layout.cardGap;
  const cardWidth = (contentWidth - cardGap) / 2;
  const cardHeight = includesMonth ? 120 : 76;
  // Matches titleOffset(18) + bodyOffset(28) + chartInnerHeight(68) + 16 + 18,
  // computed again below where those constants are actually used — kept
  // here as a literal since ensureSpace needs it before that code runs.
  const chartCardHeight = 130;

  const totalSectionHeight = 20 + cardHeight + cardGap + chartCardHeight + 20;
  const adjustedStartY = ensureSpace(doc, startY, totalSectionHeight);

  let y = drawSectionHeader(
    doc,
    x,
    adjustedStartY,
    contentWidth,
    '01',
    'Eviction numbers',
    'Households and individuals evicted, year-to-date and this month',
  );

  // --- Metric cards row ---
  drawMetricCard(doc, {
    rect: { x, y, width: cardWidth, height: cardHeight },
    label: 'Households affected',
    ytdValue: formatNumber(metrics.ytdHouseholds),
    mtdValue: includesMonth && metrics.mtdHouseholds !== null ? formatNumber(metrics.mtdHouseholds) : undefined,
    trendPct: metrics.householdsYoyPct,
    icon: houseIcon,
    iconImage: icons?.households,
  });
  drawMetricCard(doc, {
    rect: { x: x + cardWidth + cardGap, y, width: cardWidth, height: cardHeight },
    label: 'Individuals affected',
    ytdValue: formatNumber(metrics.ytdIndividuals),
    mtdValue: includesMonth && metrics.mtdIndividuals !== null ? formatNumber(metrics.mtdIndividuals) : undefined,
    trendPct: metrics.individualsYoyPct,
    icon: personIcon,
    iconImage: icons?.individuals,
  });

  y += cardHeight + cardGap;

  // --- Trend charts: plot the FULL available trend (all months up to the
  // cutoff) — only the side data list caps at the most recent 5 months. ---
  const plotted = metrics.monthlySeries.filter((m) => !m.isFuture);
  const householdValues = plotted.map((m) => m.households);
  const individualValues = plotted.map((m) => m.individuals);
  const monthLabels = plotted.map((m) => m.month);

  const summaryItems = plotted.slice(-MAX_CHART_POINTS);
  const mostRecentSummaryIndex = summaryItems.length - 1;

  const peakThreshold = Math.max(...householdValues) * 0.85;
  const highlight = householdValues.map((v) => v >= peakThreshold);

  const lowestIndex = individualValues.indexOf(Math.min(...individualValues));

  // Vertical rhythm inside each chart card, all relative to the card's top (y):
  // title -> (10pt gap) -> chart/list body -> (16pt gap) -> legend -> (18pt bottom pad) -> border
  const titleOffset = 18;
  const bodyOffset = 28;
  const chartInnerHeight = 68;
  const legendOffset = bodyOffset + chartInnerHeight + 16; // y + 112
  // (chartCardHeight already computed above, before drawSectionHeader, for ensureSpace)
  // Chart gets 75% of the card's inner width, the month/value summary list gets 25%.
  const chartPlotWidth = (cardWidth - 24) * 0.75;
  const listGap = 14;
  const listX = x + 12 + chartPlotWidth + listGap;
  const listWidth = cardWidth - 24 - chartPlotWidth - listGap;

  // Households — bar chart (full trend) + 5-month summary list
  const barCardRect = { x, y, width: cardWidth, height: chartCardHeight };
  doc.setDrawColor(...colors.stone200);
  doc.setLineWidth(0.5);
  doc.roundedRect(barCardRect.x, barCardRect.y, barCardRect.width, barCardRect.height, layout.cardRadius, layout.cardRadius, 'D');
  drawCardTitle(doc, x + 12, y + titleOffset, 'Households, monthly trend (bar)');
  drawBarChart(doc, {
    rect: { x: x + 12, y: y + bodyOffset, width: chartPlotWidth, height: chartInnerHeight },
    values: householdValues,
    labels: monthLabels,
    highlight,
  });
  doc.setDrawColor(...colors.stone200);
  doc.line(listX - 7, y + bodyOffset, listX - 7, y + bodyOffset + chartInnerHeight);
  drawSummaryCaption(doc, listX, y + titleOffset);
  drawDataList(
    doc,
    listX,
    y + bodyOffset + 7,
    listWidth,
    summaryItems.map((m, i) => ({
      label: m.month,
      value: formatNumber(m.households),
      highlight: i === mostRecentSummaryIndex,
    })),
  );
  drawLegend(doc, x + 12, y + legendOffset, [
    { color: colors.accent, label: 'Peak months' },
    { color: colors.accentLight, label: 'Below average' },
  ]);

  // Individuals — line chart (full trend) + 5-month summary list
  const lineCardX = x + cardWidth + cardGap;
  doc.roundedRect(lineCardX, y, cardWidth, chartCardHeight, layout.cardRadius, layout.cardRadius, 'D');
  drawCardTitle(doc, lineCardX + 12, y + titleOffset, 'Individuals, monthly trend (line)');
  drawLineChart(doc, {
    rect: { x: lineCardX + 12, y: y + bodyOffset, width: chartPlotWidth, height: chartInnerHeight },
    values: individualValues,
    labels: monthLabels,
    markIndex: lowestIndex,
  });
  const lineListX = lineCardX + 12 + chartPlotWidth + listGap;
  doc.line(lineListX - 7, y + bodyOffset, lineListX - 7, y + bodyOffset + chartInnerHeight);
  drawSummaryCaption(doc, lineListX, y + titleOffset);
  drawDataList(
    doc,
    lineListX,
    y + bodyOffset + 7,
    listWidth,
    summaryItems.map((m, i) => ({
      label: m.month,
      value: formatNumber(m.individuals),
      highlight: i === mostRecentSummaryIndex,
    })),
  );
  drawLegend(doc, lineCardX + 12, y + legendOffset, [
    { color: colors.stone500, label: 'Monthly count' },
    { color: colors.accent, label: 'Lowest month', shape: 'circle' },
  ]);

  return y + chartCardHeight + 20; // gap before next section
}